import React, { useEffect, useState } from 'react';
import {
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  TypographyProps,
} from '@mui/material';

import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';

import CancelIcon from '@mui/icons-material/Cancel';
import { Countries } from '../../../../constants';
import { isMobileScreen } from '../../../../helper';

const buttonProps: ButtonProps = {
  style: { fontWeight: 600, padding: '6px 40px', marginLeft: '15px' },
  variant: 'contained',
};

const label: TypographyProps = {
  variant: 'subtitle2',
  fontWeight: 600,
  sx: { mb: '2px' },
};

const grades = [
  {
    grade: 'Grade 9/Year 10',
  },
  {
    grade: 'Grade 10/Year 11',
  },
];

// DATA
const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  ? Intl.DateTimeFormat().resolvedOptions().timeZone
  : 'Asia/Singapore';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let defaultCountryCode: any = 'SG';

const defaultUserTimezone = Countries.find(
  (country) => country.value === timezone
);

if (defaultUserTimezone && defaultUserTimezone.countryCode) {
  defaultCountryCode = defaultUserTimezone.countryCode;
}

const isEmailValid = (email: string) => {
  const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return email.toLocaleLowerCase().match(regex);
};

export interface IDiagnosticState {
  email: string;
  fullName: string;
  phoneNumber: string;
  grade: string;
  country: string;
}

interface LeadDetailsModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (values: IDiagnosticState) => void;
}

const initialState: IDiagnosticState = {
  email: '',
  fullName: '',
  phoneNumber: '',
  grade: '',
  country: timezone,
};

const defaultErrorData = {
  visible: false,
  message: '',
};

const LeadDetailsModal: React.FC<LeadDetailsModalProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const isMobile = isMobileScreen();

  // State
  const [state, setState] = useState(initialState);
  const [errors, setErrors] = useState({
    fullName: { ...defaultErrorData },
    email: { ...defaultErrorData },
    phoneNumber: { ...defaultErrorData },
  });

  useEffect(() => {
    if (state.fullName) {
      let _nameError = { ...defaultErrorData };

      if (state.fullName.length < 3) {
        _nameError = {
          visible: true,
          message: 'Name should have at least 3 characters',
        };
      }

      setErrors((prevState) => ({
        ...prevState,
        fullName: _nameError,
      }));
    }
  }, [state.fullName]);

  useEffect(() => {
    if (state.email) {
      let _emailError = { ...defaultErrorData };

      if (!isEmailValid(state.email)) {
        _emailError = {
          visible: true,
          message: 'Invalid Email',
        };
      }

      setErrors((prevState) => ({
        ...prevState,
        email: _emailError,
      }));
    }
  }, [state.email]);

  useEffect(() => {
    if (state.phoneNumber) {
      let _nameError = { ...defaultErrorData };

      if (state.phoneNumber.length < 9) {
        _nameError = {
          visible: true,
          message: 'Phone Number should have at least 9 numbers',
        };
      }

      setErrors((prevState) => ({
        ...prevState,
        phoneNumber: _nameError,
      }));
    }
  }, [state.phoneNumber]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;
    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleGradeChange = (event: SelectChangeEvent) => {
    setState((prevState) => ({
      ...prevState,
      grade: event.target.value,
    }));
  };

  const submitButtonDisabled =
    state.fullName.trim().length < 3 ||
    state.email.length === 0 ||
    !isEmailValid(state.email) ||
    state.phoneNumber.trim().length < 9 ||
    state.grade === '';

  return (
    <Dialog
      open={open}
      PaperProps={{
        sx: {
          minWidth: { xs: 'unset', sm: '700px', md: '800px', lg: '900px' },
          maxWidth: { xs: 'unset', sm: '700px', md: '800px', lg: '900px' },
        },
      }}
    >
      <Stack direction="row">
        <div style={{ width: isMobile ? '100%' : '60%' }}>
          <DialogTitle fontWeight="600" align="center">
            Discover Your Learning Profile
            {!isMobile && (
              <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{
                  position: 'absolute',
                  right: 15,
                  top: 15,
                  color: 'white',
                  padding: 0,
                }}
              >
                <CancelIcon style={{ width: '30px', height: '30px' }} />
              </IconButton>
            )}
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              fontSize="12px"
              fontWeight="600"
              align="center"
              marginBottom="30px"
            >
              Fill in Your Details to Receive a Customized Diagnostic Assessment
              Report
            </DialogContentText>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <FormControl fullWidth>
                  <Typography {...label}>Full Name *</Typography>
                  <TextField
                    size="small"
                    name="fullName"
                    type="text"
                    placeholder="Type your full name"
                    onChange={handleInputChange}
                    error={errors.fullName.visible}
                    helperText={errors.fullName.message || ' '}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <FormControl fullWidth>
                  <Typography {...label}>Email *</Typography>
                  <TextField
                    size="small"
                    name="email"
                    type="email"
                    placeholder="example@gmail.com"
                    onChange={handleInputChange}
                    error={errors.email.visible}
                    helperText={errors.email.message || ' '}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={12} md={7} lg={7}>
                <Typography {...label}>Phone Number *</Typography>
                <PhoneInput
                  international
                  withCountryCallingCode
                  defaultCountry={defaultCountryCode}
                  placeholder="Enter phone number"
                  value={state.phoneNumber}
                  onChange={(value) => {
                    if (value) {
                      setState((prevState) => ({
                        ...prevState,
                        phoneNumber: value,
                      }));
                    }
                  }}
                  className="phone-number-input-custom-styles"
                />
                <FormHelperText
                  style={{ marginLeft: '14px', color: '#FF2B2D' }}
                >
                  {errors.phoneNumber.message || ' '}
                </FormHelperText>
              </Grid>
              <Grid item xs={12} sm={12} md={5} lg={5}>
                <FormControl fullWidth>
                  <Typography {...label}>Grade *</Typography>
                  <Select
                    size="small"
                    value={state.grade}
                    onChange={handleGradeChange}
                    displayEmpty
                  >
                    <MenuItem value="">Select</MenuItem>
                    {grades.map((ele, index) => (
                      <MenuItem key={index} value={ele.grade}>
                        {ele.grade}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions style={{ padding: '24px' }}>
            <Button {...buttonProps} color="inherit" onClick={onClose}>
              Cancel
            </Button>
            <Button
              {...buttonProps}
              color="primary"
              disabled={submitButtonDisabled}
              onClick={() => onConfirm(state)}
            >
              Submit
            </Button>
          </DialogActions>
        </div>
        {!isMobile && (
          <div
            style={{
              width: '40%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              background: 'linear-gradient(180deg, #01B8AF 0%, #007B76 100%)',
            }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/online-test-girl.png`}
              alt="countdown-timer"
              style={{ width: '70%', maxWidth: '90vw' }}
            />
            <Typography
              color="white"
              fontSize="12px"
              width="80%"
              marginTop="20px"
            >
              &quot;Every child has a different learning style and pace. Each
              child is unique, not only capable of learning but also capable of
              succeeding.&quot; - Robert John Meehan
            </Typography>
          </div>
        )}
      </Stack>
    </Dialog>
  );
};

export default LeadDetailsModal;
